import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    FormControlLabel,
    Checkbox,
    Alert,
    Chip,
    Grid,
    Divider
} from '@mui/material';
import ExitProcess from './ExitProcess';
import RehireProcess from './RehireProcess';
import axios from 'axios';
import { axiosInstance, localAxiosGet } from '../../../../Axios/axios';

const ExitRehireStatus = ({
    initialData,
    userId,
    isRehireMode,
    onStartRehire,
    onRehireComplete,
    rehireChecklist,
    rehireDetails,
    onNavigateToTab,
    onUpdateRehireChecklist,
    onUpdateRehireDetails,
}) => {
    const [exitDetailsData, setExitDetailsData] = useState(null);
    const [rehireDetailsData, setRehireDetailsData] = useState(null);
    const [isTerminationMode, setIsTerminationMode] = useState(false);
    const [exitStatus, setExitStatus] = useState('');
    const [loading, setLoading] = useState(false);

    // ✅ FIX: Get the first item from the array since exitDetails is an array
    const exitDetails = exitDetailsData?.[0] || null;
    
    const apiEmployeeStatus = exitDetails?.employee_status;
    const actualEmployeeStatus = apiEmployeeStatus || initialData?.status || 'inactive';
    const isEmployeeInactive = actualEmployeeStatus === 'inactive';
    const isEmployeeActive = actualEmployeeStatus === 'active';

    // ✅ FIX: Use the correct exitStatus from the array item
    const isTerminationInProgress = exitStatus === 'Initiate' || exitStatus === 'InProgress';
    const isTerminationCompleted = exitStatus === 'Completed';

    // ✅ Fetch exit and rehire details
    const fetchExitDetails = async () => {
        if (!userId) return;
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await axiosInstance(localAxiosGet(`/hr/getEmployeeExitRehireDetails/${userId}`, token));

            if (response.data?.status === 'SUCCESS') {
                const { exitDetails, personalDetails } = response.data.result;
                
                // ✅ FIX: exitDetails is an array, so set it directly
                setExitDetailsData(exitDetails || null);
                setRehireDetailsData(personalDetails || null);
                
                // ✅ FIX: Get exit_status from the first item in the array
                const currentExitStatus = exitDetails?.[0]?.exit_status || '';
                setExitStatus(currentExitStatus);

                // ✅ Automatically set termination mode if process is already initiated
                if (currentExitStatus === 'Initiate' || currentExitStatus === 'InProgress') {
                    setIsTerminationMode(true);
                } else {
                    setIsTerminationMode(false);
                }
            }
        } catch (error) {
            console.error("Error fetching exit/rehire details:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchExitDetails();
    }, [userId]);

    const handleRehireComplete = (newRehireDetails) => {
        setRehireDetailsData(newRehireDetails);
        if (onStartRehire) onStartRehire(false);
        fetchExitDetails();
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
                Employee Status Management
            </Typography>

            <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
                {/* ✅ Header with current employee status */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Current Employee Status:
                    </Typography>

                    <Chip
                        label={actualEmployeeStatus.toUpperCase()}
                        color={actualEmployeeStatus === 'active' ? 'success' : 'default'}
                        sx={{ fontWeight: 600 }}
                    />

                    {isTerminationInProgress && (
                        <Chip
                            label="Termination/Resignation in Process"
                            color="warning"
                            size="small"
                            sx={{ fontWeight: 600 }}
                        />
                    )}
                </Box>

                {/* ✅ Alert for ongoing termination */}
                {isEmployeeActive && isTerminationInProgress && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>Termination or Resignation is currently in process.</strong><br />
                        Please complete the process before initiating rehire.
                    </Alert>
                )}

                {/* ✅ FIXED: Termination checkbox - ONLY show if employee is ACTIVE and no termination process exists */}
                {isEmployeeActive && !isTerminationInProgress && !isTerminationCompleted && (
                    <Box sx={{ mt: 2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isTerminationMode}
                                    onChange={(e) => setIsTerminationMode(e.target.checked)}
                                    color="error"
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body1" fontWeight={600}>
                                        Start Termination or Resignation Process
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Check this to initiate termination/resignation flow
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box>
                )}

                {/* ✅ Rehire checkbox - ONLY show if employee is INACTIVE and termination is completed */}
                {!isRehireMode &&
                    isEmployeeInactive &&
                    isTerminationCompleted && (
                        <Box sx={{ mt: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isRehireMode}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            onStartRehire(checked);

                                            if (checked) {
                                                onUpdateRehireChecklist({
                                                    personal: false,
                                                    employee: false,
                                                    compensation: false,
                                                    policies: false,
                                                    banking: false,
                                                    documents: false,
                                                });
                                                onUpdateRehireDetails({
                                                    rehireDate: '',
                                                    rehireReason: ''
                                                });
                                            }
                                        }}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body1" fontWeight={600}>
                                            Start Rehire Process
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Check this to begin rehiring this employee after termination/resignation completion.
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>
                    )}
            </Paper>

            <Box sx={{ mt: 3 }}>
                {/* ✅ Rehire Flow */}
                {isRehireMode ? (
                    <RehireProcess
                        initialData={{
                            ...initialData,
                            rehireChecklist,
                            rehireDetails
                        }}
                        userId={userId}
                        onNavigateToTab={onNavigateToTab}
                        onUpdateRehireChecklist={onUpdateRehireChecklist}
                        onUpdateRehireDetails={onUpdateRehireDetails}
                        onRehireComplete={handleRehireComplete}
                    />
                ) : (
                    <>
                        {/* ✅ Show ExitProcess if termination is in progress OR user manually checked termination */}
                        {isTerminationInProgress || isTerminationMode ? (
                            <ExitProcess userId={userId} onUpdate={fetchExitDetails} />
                        ) : (
                            /* ✅ Show termination/rehire details ONLY when termination is completed */
                            (
                                <>
                                    {/* ✅ Previous Termination Details Section */}
                                    {isTerminationCompleted && exitDetails && (
                                        <Box sx={{ mb: 4, p: 2, border: '1px solid #ddd', borderRadius: 2, backgroundColor: '#fafafa' }}>
                                            <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                                                Termination or Resignation Details
                                            </Typography>

                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={4}>
                                                    <Typography variant="subtitle2" color="textSecondary">Type</Typography>
                                                    <Typography variant="body1">{exitDetails.type || '-'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <Typography variant="subtitle2" color="textSecondary">Date of Notice</Typography>
                                                    <Typography variant="body1">{exitDetails.date_of_notice || '-'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <Typography variant="subtitle2" color="textSecondary">Final Date of Exit</Typography>
                                                    <Typography variant="body1">{exitDetails.final_date_of_exit || '-'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <Typography variant="subtitle2" color="textSecondary">Notice Period (Days)</Typography>
                                                    <Typography variant="body1">{exitDetails.notice_period_days || '-'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={8}>
                                                    <Typography variant="subtitle2" color="textSecondary">Reason for Exit</Typography>
                                                    <Typography variant="body1">{exitDetails.reason_for_exit || '-'}</Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Divider sx={{ my: 2 }} />
                                                    <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                                                    <Typography variant="body1">{exitDetails.exit_status || 'Unknown'}</Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    )}

                                    {/* ✅ Previous Rehire Details Section (if exists) */}
                                    {rehireDetailsData && (rehireDetailsData.rehire_date || rehireDetailsData.rehire_reason) && (
                                        <Box sx={{ mb: 4, p: 2, border: '1px solid #ddd', borderRadius: 2, backgroundColor: '#e8f5e9' }}>
                                            <Typography variant="h6" color="success.main" sx={{ mb: 2 }}>
                                                Rehire Details
                                            </Typography>

                                            <Grid container spacing={2}>
                                                {rehireDetailsData.rehire_date && (
                                                    <Grid item xs={12} sm={4}>
                                                        <Typography variant="subtitle2" color="textSecondary">Rehire Date</Typography>
                                                        <Typography variant="body1">{rehireDetailsData.rehire_date}</Typography>
                                                    </Grid>
                                                )}
                                                {rehireDetailsData.rehire_reason && (
                                                    <Grid item xs={12} sm={8}>
                                                        <Typography variant="subtitle2" color="textSecondary">Rehire Reason</Typography>
                                                        <Typography variant="body1">{rehireDetailsData.rehire_reason}</Typography>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Box>
                                    )}
                                </>
                            )
                        )}

                        {/* ✅ Show empty state only when there's no active process AND no completed process */}
                        {!isTerminationInProgress && !isTerminationMode && !isTerminationCompleted && (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                                No active termination or rehire process.
                            </Typography>
                        )}
                    </>
                )}
            </Box>
        </Container>
    );
};

export default ExitRehireStatus;

